import { useQuery } from '@tanstack/react-query';
import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from 'next';
import { useState } from 'react';
import GNOTrainingBanner from '../../components/careerguide/GNOTrainingBanner';
import ActiveFilters from '../../components/shared/ActiveFilters';
import ShowingXResults from '../../components/shared/ShowingXResults';
import SearchBox from '../../components/training/SearchBox';
import TrainingFiltersAndSorts from '../../components/training/TrainingFiltersAndSorts';
import TrainingResults from '../../components/training/TrainingResults';
import GNOFooter from '../../components/wherewego/Footer';
import GNOHeader from '../../components/wherewego/Header';
import getNumberOfTrainingResultsRequest from '../../lib/api/getNumberOfTrainingResults';
import getProgramsRequest from '../../lib/api/getProgramsRequest';
import excludedCatalogs from '../../lib/config/project/excludedCatalogs';
import sortOptions from '../../lib/config/project/sortOptions';
import navGetSSP from '../../lib/gssp/navGetSSP';
import useApplySort from '../../lib/hooks/applySort';
import useClearAllFilters from '../../lib/hooks/clearAllFilters';
import initUser from '../../lib/hooks/initUser';
import useSeeAllReuslts from '../../lib/hooks/seeAllResults';
import Head from 'next/head';
import { siteTitle } from '../../lib/config/project/meta';
import { useRouter } from 'next/router';

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  return navGetSSP(ctx);
};

export type SearchParams = {
  location: string;
  careerField: string;
  page: string;
  format: string;
  hours: string;
  requirements: string;
  programType: string;
  sortBy: string;
  age: string;
  limit: string;
  search: string;
  program: string;
  provider: string;
};

const GNOTrainings: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ serverSeeker, serverUser, query: serverQuery }) => {
  initUser(serverUser, serverSeeker);

  const router = useRouter();

  const { query } = router;
  const [page, setPage] = useState(1);
  // Currently selected sort
  const [selectedSort, setSelectedSort] = useState(sortOptions[0]);
  // All of the potential filters that have not been applied yet
  const [potentialFilters, setPotentialFilters] = useState({} as any);
  const { data: totalNumberOfResults } = useQuery(
    ['number-of-training-results', JSON.stringify(query)],
    () => getNumberOfTrainingResultsRequest({ ...query, region: 'all' }),
  );
  const { data: _trainingResults } = useQuery([JSON.stringify(query)], () => {
    return getProgramsRequest({
      ...query,
      excludedCatalogs,
    });
  });

  const { applySort } = useApplySort(setSelectedSort, setPage, sortOptions);
  const { seeAllResults } = useSeeAllReuslts(
    setPage,
    potentialFilters,
    'training',
  );
  const resetState = () => {
    // setResults([]);
    setPotentialFilters({});
    // setIsComplete(false);
    setPage(1);
  };
  const { clearAllFilters } = useClearAllFilters('training', resetState);
  return (
    <>
      <Head>
        <title>{`Training Programs | ${siteTitle}`}</title>
        <meta
          name="description"
          content="Find career training programs in Greater New Orleans. Compare costs, duration, and outcomes to choose the right program."
        />
      </Head>
      <GNOHeader />
      <main>
        <GNOTrainingBanner />
        <section id="results" className="container px-4 py-10 mx-auto">
          <SearchBox
            potentialFilters={potentialFilters}
            setPotentialFilters={setPotentialFilters}
            setPage={setPage}
          />
          <p>Filter by:</p>
          <TrainingFiltersAndSorts
            selectedSort={selectedSort}
            potentialFilters={potentialFilters}
            clearAllFilters={clearAllFilters}
            setPotentialFilters={setPotentialFilters}
            setPage={setPage}
            applySort={applySort}
            seeAllResults={seeAllResults}
            totalNumberOfResults={totalNumberOfResults || 0}
            serverQuery={serverQuery}
          />
          <ActiveFilters page={'training'} clearAllFilters={clearAllFilters} />
          <div className="m-4">
            <ShowingXResults totalNumberOfResults={totalNumberOfResults || 0} />
          </div>
          <section>
            <TrainingResults page={page} setPage={setPage} />
          </section>
        </section>
      </main>
      <GNOFooter />
    </>
  );
};

export default GNOTrainings;
